import { Box, alpha, styled } from '@mui/material'
import { IPositionType } from 'src/generated-types'
import { PositionTypeTab } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

interface PositionTypeTabsProps {
  tabs: PositionTypeTab[]
  activeType: IPositionType
  onSelect: (type: IPositionType) => void
}

const PositionTypeTabs: React.FC<PositionTypeTabsProps> = ({ tabs, activeType, onSelect }) => (
  <TabRow role="tablist">
    {tabs.map(tab => {
      const isActive = tab.type === activeType
      const progressPercent = tab.totalCount ? (tab.configuredCount / tab.totalCount) * 100 : 0
      return (
        <TypeTab
          key={tab.type}
          type="button"
          role="tab"
          aria-selected={isActive}
          active={isActive}
          onClick={() => onSelect(tab.type)}
        >
          <span>{tab.label}</span>
          <TabProgressText active={isActive}>
            {tab.configuredCount}/{tab.totalCount}
          </TabProgressText>
          {tab.selectedCount > 0 && <SelectedBadge active={isActive}>{tab.selectedCount}</SelectedBadge>}
          <ProgressTrack active={isActive}>
            <ProgressFill active={isActive} sx={{ width: `${progressPercent}%` }} />
          </ProgressTrack>
        </TypeTab>
      )
    })}
  </TabRow>
)

export default PositionTypeTabs

const forwardActive = { shouldForwardProp: (prop: PropertyKey) => prop !== 'active' }

const TabRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 0.5),
  overflowX: 'auto',
  [theme.breakpoints.up('md')]: { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
}))

const TypeTab = styled(
  'button',
  forwardActive,
)<{ active: boolean }>(({ theme, active }) => {
  const primary = getThemeColor(theme, MUIThemeColors.primaryMain)
  return {
    position: 'relative',
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 34,
    padding: theme.spacing(0, 1.25),
    border: `1px solid ${active ? primary : getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
    borderRadius: 6,
    backgroundColor: active ? primary : getThemeColor(theme, MUIThemeColors.white),
    color: active ? getThemeColor(theme, MUIThemeColors.white) : primary,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1,
    cursor: 'pointer',
    fontFamily: 'inherit',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: active
        ? getThemeColor(theme, MUIThemeColors.primaryDark)
        : getThemeColor(theme, MUIThemeColors.backgroundMain),
    },
  }
})

const TabProgressText = styled(
  'span',
  forwardActive,
)<{ active: boolean }>(({ theme, active }) => ({
  fontSize: 11,
  fontWeight: 500,
  color: active
    ? alpha(getThemeColor(theme, MUIThemeColors.white), 0.75)
    : getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const SelectedBadge = styled(
  'span',
  forwardActive,
)<{ active: boolean }>(({ theme, active }) => ({
  minWidth: 16,
  height: 16,
  padding: theme.spacing(0, 0.5),
  borderRadius: 8,
  backgroundColor: active
    ? alpha(getThemeColor(theme, MUIThemeColors.white), 0.2)
    : getThemeColor(theme, MUIThemeColors.primaryLight),
  color: getThemeColor(theme, MUIThemeColors.white),
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '16px',
  textAlign: 'center',
}))

const ProgressTrack = styled(
  Box,
  forwardActive,
)<{ active: boolean }>(({ theme, active }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 2,
  backgroundColor: active
    ? alpha(getThemeColor(theme, MUIThemeColors.white), 0.25)
    : getThemeColor(theme, MUIThemeColors.paleBlue),
}))

const ProgressFill = styled(
  Box,
  forwardActive,
)<{ active: boolean }>(({ theme, active }) => ({
  height: '100%',
  backgroundColor: active
    ? getThemeColor(theme, MUIThemeColors.white)
    : getThemeColor(theme, MUIThemeColors.successMain),
}))
